import { rngStep, type Transport } from '@fafi/shared';

// Network-condition shim (§10.6): `?fakeLag=100&jitter=30&loss=2&dupe=1&netseed=42` wraps the real
// WsTransport with per-direction delay queues on real timers. Delivery is monotone per direction (a
// frame never overtakes the one ahead of it — TCP head-of-line semantics). `loss`/`dupe` are HARNESS
// knobs: a real WebSocket cannot drop or duplicate frames (§10.6 — they exist for future unreliable
// transports and for exercising InputHub idempotency), so use them knowingly.

export interface FakeLagOpts {
  delayMs: number;
  jitterMs: number;
  lossPct: number;
  dupe: boolean;
  seed: number;
}

/** Parse the fake-lag URL params; null when `fakeLag` is absent (no wrapping). */
export function parseFakeLag(params: URLSearchParams): FakeLagOpts | null {
  if (!params.has('fakeLag')) return null;
  return {
    delayMs: Number(params.get('fakeLag') ?? 0) || 0,
    jitterMs: Number(params.get('jitter') ?? 0) || 0,
    lossPct: Number(params.get('loss') ?? 0) || 0,
    dupe: params.get('dupe') === '1',
    seed: (Number(params.get('netseed') ?? 42) || 42) >>> 0,
  };
}

export function wrapFakeLag(inner: Transport, o: FakeLagOpts): Transport {
  let rngState = o.seed || 1;
  const draw = (): number => {
    const r = rngStep(rngState);
    rngState = r.state;
    return r.value;
  };
  const delay = (): number => Math.max(0, o.delayMs + (draw() * 2 - 1) * o.jitterMs);
  const lost = (): boolean => o.lossPct > 0 && draw() * 100 < o.lossPct;

  // monotone deliver-at per direction (TCP: no reordering). Date.now (not performance.now): vitest's
  // fake timers fake Date consistently with setTimeout, keeping the ordering test deterministic.
  let lastOut = 0;
  let lastIn = 0;
  const schedule = (dir: 'out' | 'in', fn: () => void): void => {
    const now = Date.now();
    const at = Math.max(now + delay(), dir === 'out' ? lastOut : lastIn);
    if (dir === 'out') lastOut = at;
    else lastIn = at;
    setTimeout(fn, Math.max(0, at - now));
  };

  let msgHandler: ((bytes: Uint8Array) => void) | null = null;
  inner.onMessage((bytes) => {
    if (lost()) return;
    schedule('in', () => msgHandler?.(bytes));
    if (o.dupe) schedule('in', () => msgHandler?.(bytes));
  });

  return {
    send: (bytes): void => {
      if (lost()) return;
      schedule('out', () => inner.send(bytes));
      if (o.dupe) schedule('out', () => inner.send(bytes));
    },
    onMessage: (h): void => {
      msgHandler = h;
    },
    onClose: (h): void => inner.onClose(h),
    close: (code): void => inner.close(code),
    bufferedAmount: (): number => inner.bufferedAmount?.() ?? 0,
  };
}
